import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { sortByProperty } from '@rio-cloud/rio-uikit/SortUtils';
import isEmpty from 'lodash/fp/isEmpty';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { gaPush, TRACKING_CATEGORIES } from '../../../configuration/setup/googleTagManager';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useMapResizerOnSidebarToggle } from '../../../hooks/useMapResizerOnSidebarToggle';
import { getTransformedData } from '../../app/selectors';
import { getClusterAssetIds } from '../../map/mapSelectors';
import { clusterDeselected, mapContainerSizeChanged } from '../../map/mapSlice';
import { showActiveAsset } from '../../map/mapThunks';
import { MOBILE_MAP_CLUSTER_SIDEBAR_CLASS_NAMES } from '../sidebarLayout';
import { SIDEBAR_DEFAULT_WIDTH, SIDEBAR_MIN_WIDTH, SIDEBAR_MODE_BREAKPOINT_MAP } from '../sidebarSelectors';
import { sidebarActions } from '../sidebarSlice';
import { ClusterSidebarContent } from './ClusterSidebarContent';
import { ClusterSidebarFooter } from './ClusterSidebarFooter';

type ClusterSidebarProps = {
    className?: string;
};

export const ClusterSidebar = memo(({ className = '' }: ClusterSidebarProps) => {
    const dispatch = useAppDispatch();
    const clusterAssetIds = useAppSelector(getClusterAssetIds);
    const transformedData = useAppSelector(getTransformedData);

    const updateMapContainer = useCallback(() => dispatch(mapContainerSizeChanged()), [dispatch]);
    const deselectCluster = useCallback(() => dispatch(clusterDeselected()), [dispatch]);
    const selectAssetFromCluster = useCallback(
        (assetId: string) => {
            dispatch(sidebarActions.assetSelectedFromClusterSidebar(assetId));
            dispatch(showActiveAsset);
        },
        [dispatch]
    );

    const assets = transformedData.filter(asset => clusterAssetIds.includes(asset.vehicleId));
    const hasAssets = !isEmpty(assets);

    const isMobile = useIsMobile();
    const mobileClassNames = isMobile ? MOBILE_MAP_CLUSTER_SIDEBAR_CLASS_NAMES : '';

    useMapResizerOnSidebarToggle(hasAssets, updateMapContainer);

    if (!hasAssets) {
        return null;
    }

    const sortedAssets = sortByProperty(assets, 'name');

    const handleClose = () => {
        updateMapContainer();
        deselectCluster();
        gaPush({
            category: TRACKING_CATEGORIES.CLUSTER_DETAILS,
            action: 'Cluster Details Closed',
            label: 'Closed Cluster Details',
        });
    };
    const handleSelectAsset = (assetId: string) => {
        selectAssetFromCluster(assetId);
        gaPush({
            category: TRACKING_CATEGORIES.CLUSTER_DETAILS,
            action: 'Cluster Asset Clicked',
            label: 'Clicked Cluster Asset',
        });
    };

    return (
        <Sidebar
            title={<FormattedMessage id='intl-msg:livemonitor.map.clusterDetails.title' />}
            className={`ClusterDetails ${mobileClassNames} ${className}`}
            titleClassName='padding-left-10'
            closed={false}
            onClose={handleClose}
            footer={<ClusterSidebarFooter onClose={handleClose} />}
            position={Sidebar.RIGHT}
            resizable
            onResizeEnd={updateMapContainer}
            width={SIDEBAR_DEFAULT_WIDTH}
            minWidth={SIDEBAR_MIN_WIDTH}
            switchModeBreakpoint={SIDEBAR_MODE_BREAKPOINT_MAP}
            enableFullscreenToggle={false}
        >
            <ClusterSidebarContent assets={sortedAssets} onSelectAsset={handleSelectAsset} />
        </Sidebar>
    );
});

export default ClusterSidebar;
